import React, { useMemo, useState } from "react";
import { BufferGeometry, DoubleSide, Vector3 } from "three";
import { MM } from "./constants";
import { C_TRATTO_CHIARO, C_TRATTO_SCURO } from "./contrasto_texture";

// Distanza fra il contorno scuro e quello chiaro.
const SCOSTAMENTO_CONTORNO = MM(8);

export type ZonaCliccabileProps = {
  position: [number, number, number];
  slotW: number;
  slotH: number;
  spessore: number;
  onClick: () => void;
  isDoorClosed: boolean;
  hoverColor?: string;
};

function rettangolo(larghezza: number, altezza: number): BufferGeometry {
  const hw = larghezza / 2;
  const hh = altezza / 2;
  return new BufferGeometry().setFromPoints([
    new Vector3(-hw, -hh, 0),
    new Vector3( hw, -hh, 0),
    new Vector3( hw,  hh, 0),
    new Vector3(-hw,  hh, 0),
  ]);
}

export const ZonaCliccabile: React.FC<ZonaCliccabileProps> = ({
  position,
  slotW,
  slotH,
  spessore,
  onClick,
  isDoorClosed,
  hoverColor = "#ff4444",
}) => {
  const [hovered, setHovered] = useState(false);

  // Doppio contorno, uno scuro e uno chiaro: cosi' lo slot resta leggibile sia sulle
  // tinte chiare sia sui campioni a texture scuri (woodstyle), dove un tratto solo
  // finiva per confondersi con la venatura.
  const contorni = useMemo(() => ({
    esterno: rettangolo(slotW, slotH),
    interno: rettangolo(
      Math.max(slotW - SCOSTAMENTO_CONTORNO * 2, MM(1)),
      Math.max(slotH - SCOSTAMENTO_CONTORNO * 2, MM(1)),
    ),
  }), [slotW, slotH]);

  if (!isDoorClosed) return null;

  return (
    <group position={[position[0], position[1], spessore / 2 + (position[2] ?? 0) + 0.001]}>
      <mesh
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        onPointerOver={(e) => { e.stopPropagation(); setHovered(true); document.body.style.cursor = "pointer"; }}
        onPointerOut={(e) => { e.stopPropagation(); setHovered(false); document.body.style.cursor = "default"; }}
      >
        <boxGeometry args={[slotW, slotH, 0.001]} />
        <meshBasicMaterial
          color={hoverColor}
          transparent
          opacity={hovered ? 0.34 : 0.16}
          side={DoubleSide}
          depthWrite={false}
        />
      </mesh>

      {/* I contorni non partecipano al raycast: la soglia di three per le linee e' in
          metri e intercetterebbe i click destinati al pannello. */}
      <lineLoop geometry={contorni.esterno} position={[0, 0, MM(1)]} raycast={() => null}>
        <lineBasicMaterial color={C_TRATTO_SCURO} transparent opacity={hovered ? 1 : 0.85} depthWrite={false} />
      </lineLoop>
      <lineLoop geometry={contorni.interno} position={[0, 0, MM(1)]} raycast={() => null}>
        <lineBasicMaterial color={C_TRATTO_CHIARO} transparent opacity={hovered ? 1 : 0.85} depthWrite={false} />
      </lineLoop>
    </group>
  );
};
